import { useCallback, useEffect, useState } from 'react'
import { Button } from '@mui/material'
import {
  ContextualHelp,
  Form,
  Input,
  PageTitle,
  Select,
  TaskItem,
  Textarea,
} from '@components'
import { useTask } from '@hooks'
import { FORM_DATA_INITIAL } from './form-data.constant'
import './task.style.scss'

const CONFIG_TASK = {
  URGENTE_IMPORTANTE: {
    class: 'urgente-importante',
    color: '#f29166',
    name: 'Urgente e Importante',
  },
  NAO_URGENTE_IMPORTANTE: {
    class: 'nao-urgente-importante',
    color: '#f3ca40',
    name: 'N\u00e3o Urgente e Importante',
  },
  URGENTE_NAO_IMPORTANTE: {
    class: 'urgente-nao-importante',
    color: '#2e7f7b',
    name: 'Urgente e N\u00e3o Importante',
  },
  NAO_URGENTE_NAO_IMPORTANTE: {
    class: 'nao-urgente-nao-importante',
    color: '#788bf5',
    name: 'N\u00e3o Urgente e N\u00e3o Importante',
  },
}

const TASK_HELP_SUMMARY =
  'Crie suas tarefas e escolha uma prioridade inicial. Isso ajuda a decidir o que merece sua aten\u00e7\u00e3o primeiro.'

const TASK_HELP_ITEMS = [
  {
    title: 'Urgente e importante',
    description: 'Precisa de aten\u00e7\u00e3o logo e tem impacto real. Normalmente vem primeiro.',
  },
  {
    title: 'Importante, mas sem pressa',
    description: 'Vale planejar com calma. \u00c9 onde moram avan\u00e7os bons.',
  },
  {
    title: 'Urgente, mas menor',
    description: 'Resolva r\u00e1pido, delegue se puder e n\u00e3o deixe tomar o dia.',
  },
  {
    title: 'Pouco urgente e pouco importante',
    description: 'Pode esperar. \u00c0s vezes nem precisa ficar na lista.',
  },
]

const TASK_HELP_TIPS = [
  'Mudou de ideia? Depois \u00e9 s\u00f3 reorganizar na matriz.',
]

const TaskScreen = () => {
  const [taskList, setTaskList] = useState()
  const [formData, setFormData] = useState({ ...FORM_DATA_INITIAL })

  const { getTasks, createTask, updateTask, deleteTask } = useTask()
  const selectedPriorityConfig = CONFIG_TASK[formData.priority.value]
  const submitLabel = formData.task ? 'Salvar alteração' : 'Adicionar tarefa'

  const getTaskList = useCallback(() => {
    const resultado = getTasks()

    if (resultado) {
      setTaskList(resultado)
    }
  }, [getTasks])

  useEffect(() => {
    getTaskList()
  }, [getTaskList])

  const handleChangeTask = ({ task, check }) => {
    const data = check ? { ...task, estaRealizado: !task.estaRealizado } : task

    updateTask({ data, id: task.id })
    getTaskList()
  }

  const handleChangeDeleteTask = ({ id }) => {
    deleteTask(id)
    getTaskList()
  }

  const handleClickSaveAlter = task => updateTask({ data: task, id: task.id })

  const renderTasks = () => {
    if (!taskList) {
      return null
    }

    const allTasks = Object.entries(taskList).flatMap(([priority, tasks]) =>
      tasks.map(task => ({
        priority,
        task,
      }))
    )

    const sortedTasks = [
      ...allTasks.filter(({ task }) => !task.estaRealizado),
      ...allTasks.filter(({ task }) => task.estaRealizado),
    ]

    return sortedTasks.map(({ priority, task }) => (
      <TaskItem
        key={task.id}
        task={task}
        color={CONFIG_TASK[priority].color}
        className={CONFIG_TASK[priority].class}
        name={CONFIG_TASK[priority].name}
        handleChangeTask={handleChangeTask}
        handleClickSaveAlter={handleClickSaveAlter}
        isTask={true}
        handleClickDelete={handleChangeDeleteTask}
      />
    ))
  }

  const handleSubmit = ({ isValid, values }) => {
    if (formData.task) {
      const updateTaskData = {
        ...formData.task,
        descricao: values.description,
        prioridade: values.priority,
        titulo: values.title,
      }

      handleChangeTask({ task: updateTaskData })
      setFormData({ ...FORM_DATA_INITIAL })
      return
    }

    if (isValid && values) {
      const data = {
        descricao: values.description,
        prioridade: values.priority,
        titulo: values.title,
      }

      const resultado = createTask(data)

      if (resultado) {
        setFormData({ ...FORM_DATA_INITIAL })
        getTaskList()
      }
    }
  }

  const handleChange = event => {
    const { name, value } = event.target

    setFormData(currentFormData => ({
      ...currentFormData,
      [name]: {
        ...currentFormData[name],
        value,
        name,
      },
    }))
  }

  return (
    <section className="task">
      <div className="task__header">
        <PageTitle titulo="Lista de Tarefas" />
        <ContextualHelp
          className="task__help"
          title="Como usar a Lista de Tarefas"
          summary={TASK_HELP_SUMMARY}
          items={TASK_HELP_ITEMS}
          tips={TASK_HELP_TIPS}
        />
      </div>

      <Form
        className="task__composer"
        onSubmit={handleSubmit}
        formData={formData}
      >
        <div className="task__composer__top">
          <Input
            formData={formData.title}
            handleChange={handleChange}
            fullWidth
            size="small"
            label="Título"
            placeholder="Ex.: revisar planejamento da semana"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#fff',
              },
            }}
          />

          <div className="task__composer__priority-field">
            <span
              className="task__composer__priority-field__swatch"
              style={{
                backgroundColor: selectedPriorityConfig?.color || 'transparent',
                opacity: selectedPriorityConfig ? 1 : 0,
              }}
            />
            <Select
              formData={formData.priority}
              handleChange={handleChange}
              fullWidth
              size="small"
              hideLabel={true}
              displayEmpty
              renderValue={value =>
                value ? (
                  selectedPriorityConfig?.name
                ) : (
                  <span className="task__composer__priority-placeholder">
                    Prioridade
                  </span>
                )
              }
              formControlProps={{
                fullWidth: true,
                sx: {
                  margin: 0,
                },
              }}
              sx={{
                width: '100%',
                borderRadius: '12px',
                backgroundColor: '#fff',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '20px',
                  paddingLeft: selectedPriorityConfig ? '42px' : '14px',
                },
              }}
            />
          </div>
        </div>

        <Textarea
          className="task__composer__textarea"
          formData={formData.description}
          handleChange={handleChange}
          minRows={1}
          placeholder="Adicione alguma observação."
          style={{
            background: '#fff',
          }}
        />

        <div className="task__composer__actions">
          <Button
            sx={{
              color: 'white',
              minWidth: 148,
              height: 40,
              borderRadius: '999px',
              boxShadow: 'none',
              fontWeight: 600,
              fontSize: '13px',
              paddingInline: '18px',
            }}
            variant="contained"
            type="submit"
          >
            {submitLabel}
          </Button>
        </div>
      </Form>

      <ul className="task__lista">{renderTasks()}</ul>
    </section>
  )
}

export { TaskScreen }
